/**
 * Оболочка приложения (§16.5).
 *
 * Экран входа есть — /login (§16.3): ИТ-служба экосистемы доступ не даст,
 * рабочий вход на первую очередь — ФИО и рабочий телефон, своя форма.
 * Поэтому /login — единственный маршрут, который не требует сессии и не
 * запускает загрузку анкеты/профиля; всё остальное — по §16.5:
 * /clients, /intake/{id}, /result/{id}. Открытие приложения сразу по
 * ссылке на конкретную анкету (с другого устройства, §16.6) работает
 * благодаря обычным URL, а не только состоянию в памяти.
 */

import { useCallback, useEffect, useState } from 'react'
import { Link, Navigate, Route, Routes, useParams } from 'react-router-dom'
import { SessionExpired, api } from './api/client'
import type { Questionnaire } from './api/types'
import { AdminPage } from './components/AdminPage'
import { ClientsList } from './components/ClientsList'
import { DeviceInterstitial, wasDeviceTrustAsked } from './components/DeviceInterstitial'
import { IntakeWizard } from './components/IntakeWizard'
import { Logo } from './components/Logo'
import { LoginPage } from './components/LoginPage'
import { ResultPage } from './components/ResultPage'
import { SessionsPanel } from './components/SessionsPanel'

interface Employee {
  full_name: string | null
  branch: string | null
  role: string
}

function IntakeRoute({ questionnaire }: { questionnaire: Questionnaire }) {
  const { draftId } = useParams<{ draftId: string }>()
  if (draftId == null) return <Navigate to="/clients" replace />
  return <IntakeWizard draftId={draftId} questionnaire={questionnaire} />
}

function AuthenticatedApp() {
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null)
  const [employee, setEmployee] = useState<Employee | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [sessionsOpen, setSessionsOpen] = useState(false)
  const [deviceTrustAsked, setDeviceTrustAsked] = useState(wasDeviceTrustAsked)

  const handleError = useCallback((err: unknown) => {
    if (err instanceof SessionExpired) {
      // Решение о доступе принимает бэкенд; фронтенд лишь выполняет переход.
      window.location.href = err.portalUrl
      return
    }
    setError(err instanceof Error ? err.message : String(err))
  }, [])

  useEffect(() => {
    Promise.all([api.questionnaire(), api.me()])
      .then(([loaded, me]) => {
        setQuestionnaire(loaded)
        setEmployee(me)
      })
      .catch(handleError)
  }, [handleError])

  return (
    <>
      {/* Шапка в стиле сайта «Этажей»: белая полоса с логотипом и тёмное меню. */}
      <header className="site-header">
        <div className="site-header-inner">
          <Link to="/clients" className="brand">
            <Logo />
            <span className="brand-product">
              Ипотечный ассистент
              <span className="brand-city">{employee?.branch ?? 'Стерлитамак'}</span>
            </span>
          </Link>
          <button className="header-user" onClick={() => setSessionsOpen((v) => !v)}>
            {employee?.full_name ?? '…'}
          </button>
        </div>
      </header>
      <nav className="site-nav">
        <div className="site-nav-inner">
          <Link to="/clients">Мои клиенты</Link>
          {(employee?.role === 'manager' || employee?.role === 'admin') && (
            <Link to="/admin">Админ-раздел</Link>
          )}
        </div>
      </nav>

    <div className="layout">

      {sessionsOpen && <SessionsPanel onClose={() => setSessionsOpen(false)} />}

      {error != null && <p className="error">{error}</p>}

      {questionnaire != null && !deviceTrustAsked && (
        <DeviceInterstitial onDone={() => setDeviceTrustAsked(true)} />
      )}

      {questionnaire != null && deviceTrustAsked && (
        <Routes>
          <Route path="/" element={<Navigate to="/clients" replace />} />
          <Route path="/clients" element={<ClientsList />} />
          <Route path="/intake/:draftId" element={<IntakeRoute questionnaire={questionnaire} />} />
          <Route path="/result/:draftId" element={<ResultPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="*" element={<Navigate to="/clients" replace />} />
        </Routes>
      )}

      <p className="disclaimer">
        Расчёт носит информационный характер и не является офертой. Итоговые условия
        определяет банк по результатам андеррайтинга и оценки объекта. Приложение
        не заменяет андеррайтинг банка.
      </p>
    </div>
    </>
  )
}

export function App() {
  return (
    <Routes>
      {/* Единственный маршрут без сессии (§16.3) — своя страница входа. */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/*" element={<AuthenticatedApp />} />
    </Routes>
  )
}
